import { CloseOutlined, DatabaseOutlined, FolderOpenOutlined, HddOutlined, LoadingOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons'

import { IDirectoryUsage, IDiskUsage } from '@api/systems/api.types'
import { ComponentsHeader } from '@primitive/sectionHeadings'
import { Empty, Loader, Modal } from '@ui/index'
import { IconServer } from '@img/icons'
import imgEmpty from '@img/empty.svg'
import imgAccessDenied from '@img/access_denied.svg'
import { getStorageWarnings } from '../../../services/storage-health.service'
import type { IStorageWarning, TStorageWarningID } from '../../../services/storage-health.service'

import { ISystemsUI, TDirectorySource } from './types'
import css from './ui.module.scss'


const byteUnits = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ']

export const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 Б'

    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), byteUnits.length - 1)
    const value = bytes / (1024 ** unitIndex)

    return `${new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits: 1,
    }).format(value)} ${byteUnits[unitIndex]}`
}

const formatPercent = (value: number) => new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
}).format(value)

interface IStorageWidget {
    title: string
    description: string
    storage: IDiskUsage | null
    variant: 'data' | 'system'
    warning?: IStorageWarning
    onClick?: () => void
}

const StorageWidgetContent = ({title, description, storage, variant, warning, onClick}: IStorageWidget) => {
    return <>
        <div className={css.widgetHeader}>
            <span className={css.widgetIcon} aria-hidden='true'>
                {variant === 'data' ? <DatabaseOutlined /> : <HddOutlined />}
            </span>
            <div className={css.widgetHeading}>
                <h3>{title}</h3>
                <p>{description}</p>
            </div>
            {onClick && <RightOutlined className={css.widgetArrow} aria-hidden='true' />}
        </div>

        {warning && <div className={css.storageWarning} role='alert'>
            <WarningOutlined aria-hidden='true' />
            <div>
                <strong>КРИТИЧЕСКИ МАЛО МЕСТА</strong>
                <span>Свободно: {formatBytes(warning.storage.freeBytes)}.</span>
                <span>Необходимо освободить место: должно оставаться не менее {formatBytes(warning.minimumFreeBytes)}.</span>
            </div>
        </div>}

        {!storage
            ? <div className={css.unavailable} role='status'>
                <strong>Сервер недоступен</strong>
                <span>Повторите попытку кнопкой обновления</span>
            </div>
            : <StorageUsage storage={storage} />
        }
    </>
}

const StorageUsage = ({storage}: {storage: IDiskUsage}) => {
    const usedPercent = Math.min(100, Math.max(0, storage.usedPercent))
    const deviceTotalBytes = storage.deviceTotalBytes || storage.totalBytes
    const hasCapacityDifference = storage.totalBytes < deviceTotalBytes * .95
    const unallocatedBytes = Math.max(0, deviceTotalBytes - storage.totalBytes)

    return <>
        <div className={css.usageSummary}>
            <strong>{formatPercent(storage.usedPercent)}%</strong>
            <span>занято</span>
        </div>

        <div
            className={css.progressTrack}
            role='progressbar'
            aria-label={`Занято на ${storage.path}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={usedPercent}
        >
            <span style={{width: `${usedPercent}%`}} />
        </div>

        <dl className={css.metrics}>
            <div>
                <dt>Занято</dt>
                <dd>{formatBytes(storage.usedBytes)}</dd>
            </div>
            <div>
                <dt>Свободно</dt>
                <dd>{formatBytes(storage.freeBytes)}</dd>
            </div>
            <div>
                <dt>Объём диска</dt>
                <dd>{formatBytes(deviceTotalBytes)}</dd>
            </div>
        </dl>

        <div className={css.filesystemCapacity}>
            <span>Объём файловой системы</span>
            <strong>{formatBytes(storage.totalBytes)}</strong>
            {hasCapacityDifference && <small>
                Не распределено в файловой системе: {formatBytes(unallocatedBytes)}
            </small>}
        </div>
    </>
}

const getStorageWidgetClassName = ({variant, warning, onClick}: IStorageWidget) => [
    css.widget,
    variant === 'system' ? css.widgetSystem : '',
    warning ? css.widgetWarning : '',
    onClick ? css.widgetButton : '',
].filter(Boolean).join(' ')

const StorageWidget = (props: IStorageWidget) => props.storage && props.onClick
    ? <button
        type='button'
        className={getStorageWidgetClassName(props)}
        aria-label={`${props.title}. Открыть размеры каталогов /var/www`}
        onClick={props.onClick}
    >
        <StorageWidgetContent {...props} />
    </button>
    : <article
        className={getStorageWidgetClassName(props)}
        aria-label={props.storage ? props.title : `${props.title}. Сервер недоступен`}
    >
        <StorageWidgetContent {...props} />
    </article>

const getDirectoryServerName = (source: TDirectorySource | null) => source === 'yaskill' ? 'YASKILL' : 'CRM'

const sortDirectoryItems = (items: IDirectoryUsage[]) => [...items].sort((left, right) => {
    const isLeftReady = left.status === 'ready' && left.sizeBytes !== null
    const isRightReady = right.status === 'ready' && right.sizeBytes !== null

    if (isLeftReady && isRightReady) {
        return (right.sizeBytes || 0) - (left.sizeBytes || 0) || left.name.localeCompare(right.name, 'ru')
    }
    if (isLeftReady !== isRightReady) return isLeftReady ? -1 : 1
    if (left.status !== right.status) return left.status === 'calculating' ? -1 : 1
    return left.name.localeCompare(right.name, 'ru')
})

const DirectoryUsageRow = ({item}: {item: IDirectoryUsage}) => {
    const isCalculating = item.status === 'calculating'
    const isReady = item.status === 'ready' && item.sizeBytes !== null
    const rowClassName = isReady
        ? css.directoryRowReady
        : item.status === 'error'
            ? css.directoryRowError
            : css.directoryRowCalculating
    const statusClassName = isReady
        ? css.directoryStatusReady
        : item.status === 'error'
            ? css.directoryStatusError
            : css.directoryStatusCalculating

    return <div className={`${css.directoryRow} ${rowClassName}`} role='listitem' aria-busy={isCalculating}>
        <span className={css.directoryIcon} aria-hidden='true'>
            <FolderOpenOutlined />
        </span>
        <span className={css.directoryName}>{item.name}</span>
        <span className={`${css.directoryStatus} ${statusClassName}`}>
            {isCalculating && <LoadingOutlined className={css.directorySpinner} aria-hidden='true' />}
            {isReady
                ? <strong>{formatBytes(item.sizeBytes)}</strong>
                : item.status === 'error' ? 'Ошибка подсчёта' : 'Подсчёт...'
            }
        </span>
    </div>
}

/** UI - Система */
export const SystemsUI = ({
    isLoadingPage, isAccessDenied, storageInfo,
    isDirectoryModalOpen, directorySource, isLoadingDirectories, directoryItems,
    handleRefresh, handleOpenDirectoryModal, handleCloseDirectoryModal,
}: ISystemsUI) => {
    const warningsByID = new Map<TStorageWarningID, IStorageWarning>(
        (storageInfo ? getStorageWarnings(storageInfo) : []).map((warning) => [warning.id, warning])
    )
    const calculatedDirectories = directoryItems.filter((item) => item.status === 'ready' && item.sizeBytes !== null)
    const failedDirectoriesCount = directoryItems.filter((item) => item.status === 'error').length
    const directoriesTotalBytes = calculatedDirectories.reduce((total, item) => total + (item.sizeBytes || 0), 0)
    const totalLabel = isLoadingDirectories || failedDirectoriesCount > 0 ? 'Посчитано' : 'Всего'
    const sortedDirectoryItems = sortDirectoryItems(directoryItems)

    return <div className={css.container}>
        <ComponentsHeader
            isAccessDenied={isAccessDenied}
            headerIcon={<IconServer width='20px' height='20px' />}
            headerTitle='Система'
            btnUpdateOnClick={handleRefresh}
        />

        <main className={css.content}>
            <Loader isOpen={isLoadingPage} backdrop title='Загрузка информации о дисках...' />

            {isAccessDenied
                ? <Empty image={imgAccessDenied} title='Доступ запрещён' />
                : storageInfo
                    ? <div className={css.storageSections}>
                        <section className={css.serverSection} aria-labelledby='crm-storage-title'>
                            <h2 id='crm-storage-title'>Сервер CRM</h2>
                            <div className={css.widgets}>
                                <StorageWidget
                                    title='Хранилище CRM'
                                    description={storageInfo.dataStorage.path}
                                    storage={storageInfo.dataStorage}
                                    variant='data'
                                    warning={warningsByID.get('crm-data')}
                                />
                                <StorageWidget
                                    title='Системный диск CRM'
                                    description='/var/www'
                                    storage={storageInfo.systemStorage}
                                    variant='system'
                                    warning={warningsByID.get('crm-system')}
                                    onClick={() => handleOpenDirectoryModal('crm')}
                                />
                            </div>
                        </section>

                        <section className={css.serverSection} aria-labelledby='yaskill-storage-title'>
                            <h2 id='yaskill-storage-title'>Сервер YASKILL</h2>
                            <div className={css.widgets}>
                                <StorageWidget
                                    title='Хранилище YASKILL'
                                    description='/storage'
                                    storage={storageInfo.yaskill?.dataStorage || null}
                                    variant='data'
                                    warning={warningsByID.get('yaskill-data')}
                                />
                                <StorageWidget
                                    title='Системный диск YASKILL'
                                    description='/var/www'
                                    storage={storageInfo.yaskill?.systemStorage || null}
                                    variant='system'
                                    warning={warningsByID.get('yaskill-system')}
                                    onClick={() => handleOpenDirectoryModal('yaskill')}
                                />
                            </div>
                        </section>
                    </div>
                    : <Empty image={imgEmpty} title='Данные отсутствуют' />
            }
        </main>

        <Modal
            isOpen={isDirectoryModalOpen}
            isEsc
            isWrapper
            maxWidth='720px'
            overflowY='auto'
            className={css.directoryModal}
            onCancel={handleCloseDirectoryModal}
        >
            <div
                className='modal_container'
                role='dialog'
                aria-modal='true'
                aria-labelledby='system-directory-modal-title'
            >
                <div className={`modal_container-header ${css.modalHeader}`}>
                    <div>
                        <h4 id='system-directory-modal-title'>Каталоги /var/www на сервере {getDirectoryServerName(directorySource)}</h4>
                        <p>Размер включает всё содержимое каталога</p>
                    </div>
                    <button type='button' className={css.closeButton} aria-label='Закрыть' onClick={handleCloseDirectoryModal}>
                        <CloseOutlined />
                    </button>
                </div>

                <div className={`modal_container-body ${css.modalBody}`}>
                    <div className={css.directorySurface}>
                        {directoryItems.length === 0
                            ? isLoadingDirectories
                                ? <div className={css.directoryInitialState} role='status'>
                                    <LoadingOutlined aria-hidden='true' />
                                    <span>Получаем список каталогов...</span>
                                </div>
                                : <Empty image={imgEmpty} title='Каталоги отсутствуют' size='120px' />
                            : <div className={css.directoryList} role='list'>
                                {sortedDirectoryItems.map((item) => <DirectoryUsageRow item={item} key={item.name} />)}
                            </div>
                        }
                    </div>

                    {directoryItems.length > 0 && <div className={css.directoryFooter}>
                        <span>
                            Каталогов: {directoryItems.length} · Готово: {calculatedDirectories.length}
                            {failedDirectoriesCount > 0 ? ` · Ошибок: ${failedDirectoriesCount}` : ''}
                        </span>
                        <strong>{totalLabel}: {formatBytes(directoriesTotalBytes)}</strong>
                    </div>}
                </div>
            </div>
        </Modal>
    </div>
}